'use client';

import { useEffect, useState } from 'react';
import { Order } from '@/interface/order';
import { supabase } from '@/lib/supabase/client';

export function isKitchenDisplayOrder(order: { status?: string; number?: number | null }): boolean {
  return (order.status === 'pending' || order.status === 'ready') && order.number != null;
}

export const useOrdersDisplay = (locationId: string, currentDisplayId: string) => {
  const [orders, setOrders] = useState<Order[]>([]);

  useEffect(() => {
    if (!locationId) return;
    if (!currentDisplayId) return;

    supabase
      .from('orders')
      .select('id, status, number, updated_at, created_at')
      .eq('location_id', locationId)
      .in('status', ['pending', 'ready'])
      .not('number', 'is', null)
      .order('created_at', { ascending: false })
      .limit(200)
      .overrideTypes<Order[]>()
      .then(res => {
        if (res.data) {
          setOrders(res.data);
        }
      });

    const ordersChannel = supabase
      .channel(`orders:location_id=eq.${locationId}:${currentDisplayId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `location_id=eq.${locationId}`,
        },
        payload => {
          switch (payload.eventType) {
            case 'UPDATE': {
              setOrders(prev => {
                const updated = payload.new as Order;
                if (isKitchenDisplayOrder(updated)) {
                  const existing = prev.find(order => order.id === updated.id);
                  if (existing) {
                    return prev.map(order => (order.id === updated.id ? updated : order));
                  }
                  return [...prev, updated];
                }
                return prev.filter(order => order.id !== payload.new.id);
              });
              break;
            }
            case 'INSERT':
              if (isKitchenDisplayOrder(payload.new as Order)) {
                setOrders(prev => [...prev, payload.new as Order]);
              }
              break;
            case 'DELETE':
              setOrders(prev => prev.filter(order => order.id !== payload.old.id));
              break;
            default:
              break;
          }
        }
      )
      .subscribe();

    return () => {
      ordersChannel.unsubscribe();
      supabase.removeChannel(ordersChannel);
      setOrders([]);
    };
  }, [currentDisplayId, locationId]);

  return { orders };
};
